import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { Options as Html2CanvasOptions } from 'html2canvas';

interface PdfGeneratorOptions extends Partial<Html2CanvasOptions> {
  scale?: number;
  useCORS?: boolean;
  allowTaint?: boolean;
  backgroundColor?: string;
  width?: number;
  height?: number;
  scrollX?: number;
  scrollY?: number;
}

interface PdfGeneratorResult {
  success: boolean;
  message?: string;
  error?: string;
}

interface HiddenElement {
  element: HTMLElement;
  originalDisplay: string;
}

export const usePdfGenerator = () => {
  const generatePDF = async (
    elementId: string,
    filename: string = 'report.pdf',
    options: PdfGeneratorOptions = {}
  ): Promise<PdfGeneratorResult> => {
    try {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error(`Element with ID '${elementId}' not found`);
      }

      // Temporarily remove all canvas elements to prevent WebGL context errors
      const canvasElements: { canvas: HTMLCanvasElement; parent: Node; nextSibling: Node | null; dataUrl?: string }[] =
        [];
      const canvases = element.querySelectorAll('canvas');

      canvases.forEach(canvas => {
        const parent = canvas.parentNode;
        const nextSibling = canvas.nextSibling;

        // Try to capture canvas content first (before removing it)
        let dataUrl: string | undefined;
        try {
          const capturedDataUrl = canvas.toDataURL('image/png');
          if (capturedDataUrl && capturedDataUrl !== 'data:,') {
            console.log('Successfully captured canvas as image for PDF');
            dataUrl = capturedDataUrl;
          }
        } catch (error) {
          console.warn('Could not capture canvas as image, will be missing from PDF:', error);
        }

        // Store info for restoration and create replacement image if needed
        if (parent) {
          canvasElements.push({ canvas, parent, nextSibling, dataUrl });

          // If we captured the canvas, insert an image replacement
          if (dataUrl) {
            const img = document.createElement('img');
            img.src = dataUrl;
            img.style.width = canvas.style.width || `${canvas.width}px`;
            img.style.height = canvas.style.height || `${canvas.height}px`;
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.setAttribute('data-canvas-replacement', 'true');

            parent.insertBefore(img, canvas);
          }

          // Remove the canvas from DOM
          parent.removeChild(canvas);
        }
      });

      // Default options
      const defaultOptions: PdfGeneratorOptions = {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        ignoreElements: element => {
          // Aggressively ignore all canvas elements to prevent WebGL errors
          return element.tagName === 'CANVAS';
        },
        foreignObjectRendering: false, // Disable foreign object rendering which might interfere
        ...options,
      };

      // Temporarily show hidden elements for PDF generation
      const hiddenElements: HiddenElement[] = [];
      const elementsToShow = element.querySelectorAll('[data-pdf-show]');
      elementsToShow.forEach(el => {
        const htmlElement = el as HTMLElement;
        if (htmlElement.style.display === 'none') {
          hiddenElements.push({
            element: htmlElement,
            originalDisplay: htmlElement.style.display,
          });
          htmlElement.style.display = 'block';
        }
      });

      // Temporarily hide elements that should not appear in PDF
      const visibleElements: HiddenElement[] = [];
      const elementsToHide = element.querySelectorAll('[data-pdf-hide]');
      elementsToHide.forEach(el => {
        const htmlElement = el as HTMLElement;
        if (htmlElement.style.display !== 'none') {
          visibleElements.push({
            element: htmlElement,
            originalDisplay: htmlElement.style.display || '',
          });
          htmlElement.style.display = 'none';
        }
      });

      // Generate canvas from HTML
      const canvas = await html2canvas(element, defaultOptions);

      // Restore canvas elements
      canvasElements.forEach(({ canvas, parent, nextSibling }) => {
        if (parent) {
          // If it was a replacement image, remove it
          const parentElement = parent as Element;
          if (parentElement.querySelector) {
            const replacementImg = parentElement.querySelector('[data-canvas-replacement]');
            if (replacementImg) {
              parent.removeChild(replacementImg);
            }
          }
          // Restore the original canvas
          parent.insertBefore(canvas, nextSibling || null);
        }
      });

      // Restore hidden elements
      hiddenElements.forEach(({ element, originalDisplay }) => {
        element.style.display = originalDisplay;
      });

      // Restore visible elements
      visibleElements.forEach(({ element, originalDisplay }) => {
        element.style.display = originalDisplay;
      });

      // Calculate PDF dimensions with margins
      const pageWidth: number = 210; // A4 width in mm
      const pageHeight: number = 297; // A4 height in mm
      const margin: number = 15; // 15mm margin on all sides
      const contentWidth: number = pageWidth - margin * 2; // Available width for content
      const contentHeight: number = pageHeight - margin * 2; // Available height for content

      const imgHeight: number = (canvas.height * contentWidth) / canvas.width;
      let heightLeft: number = imgHeight;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let position: number = 0;

      // Add first page with margins
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin + position, contentWidth, imgHeight);
      heightLeft -= contentHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin + position, contentWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      // Save the PDF
      pdf.save(filename);

      return { success: true, message: 'PDF generated successfully' };
    } catch (error) {
      console.error('PDF generation failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { success: false, error: errorMessage };
    }
  };

  return {
    generatePDF,
  };
};
